import type { PageOrientation } from './PrintTheme';

/**
 * The ten printed layouts.
 *
 * Each is a genuinely different *composition*, not a font swap: where the logo
 * sits, whether the masthead is a band or a sidebar or a diagonal, how the
 * title is presented, what decorates the page, how tables are ruled, and what
 * closes the footer. A parish picks one per document kind and every printed
 * page follows it.
 *
 * Colour always comes from the parish's own brand colour, so a template is a
 * shape that any church can wear.
 *
 * A template is a *design*, not a document. Which design a given document uses
 * is recorded per document in the print template register — the four broad
 * buckets this file used to carry (certificate / document / financial / list)
 * put baptism fields on marriage certificates and are gone.
 */

export type PrintTemplateId =
  | 'minimal' | 'banner' | 'corporate' | 'diagonal' | 'wave'
  | 'ribbon' | 'frame' | 'luxury' | 'creative' | 'premium';

/** How the masthead block is built. */
export type HeaderStyle =
  | 'plain'      // text only, hairline divider
  | 'band'       // full-width colour bar
  | 'sidebar'    // vertical colour column down the left
  | 'diagonal'   // angular two-tone wedge
  | 'wave'       // curved graphic edge
  | 'split'      // colour block beside a white block
  | 'outline'    // bordered box, no fill
  | 'stacked';   // centred, rules above and below

export type LogoPlacement = 'left' | 'right' | 'center' | 'sidebar' | 'none';
export type TitlePlacement = 'below-header' | 'in-header' | 'ribbon' | 'boxed' | 'centered-rule';
export type TableStyle = 'plain' | 'lined' | 'striped' | 'boxed' | 'headband' | 'minimal-rule';
export type FooterStyle = 'none' | 'rule' | 'band' | 'double-rule' | 'centered-note';
export type BorderStyle = 'none' | 'thin' | 'double' | 'ornamental' | 'corners';
export type Decoration = 'none' | 'corner-triangle' | 'corner-arcs' | 'side-accent' | 'gradient-bar';

export interface PrintTemplate {
  id: PrintTemplateId;
  label: string;
  /** One line describing the look, shown on the picker card. */
  blurb: string;
  /** Where this template earns its keep. */
  bestFor: string;

  // --- Composition ---
  header: HeaderStyle;
  logo: LogoPlacement;
  title: TitlePlacement;
  table: TableStyle;
  footer: FooterStyle;
  border: BorderStyle;
  decoration: Decoration;
  align: 'left' | 'center';
  /** Faint logo behind the body copy. */
  watermark: boolean;

  // --- Type ---
  bodyFont: string;
  headingFont: string;
  bodySize: string;
  lineHeight: number;
  titleTransform: 'uppercase' | 'none';
  titleSpacing: string;

  // --- Metrics ---
  pagePadding: string;
  cellPadding: string;
  headerGap: string;
  logoHeight: string;
  /** Thickness of the template's signature colour element. */
  bandHeight: string;

  orientation?: PageOrientation;
}

const SERIF = 'Georgia, "Times New Roman", serif';
const SERIF_ALT = '"Palatino Linotype", Palatino, Georgia, serif';
const SANS = 'Arial, Helvetica, sans-serif';
const SANS_ALT = '"Helvetica Neue", Helvetica, Arial, sans-serif';

export const PRINT_TEMPLATES: Record<PrintTemplateId, PrintTemplate> = {
  minimal: {
    id: 'minimal',
    label: 'Minimal White',
    blurb: 'Black text, one thin divider, nothing else. No colour at all.',
    bestFor: 'Long registers and anything printed in bulk on a mono printer.',
    header: 'plain', logo: 'center', title: 'below-header',
    table: 'minimal-rule', footer: 'rule', border: 'none',
    decoration: 'none', align: 'center', watermark: false,
    bodyFont: SANS_ALT, headingFont: SANS_ALT,
    bodySize: '13px', lineHeight: 1.6,
    titleTransform: 'none', titleSpacing: '0',
    pagePadding: '26mm 18mm', cellPadding: '8px 10px',
    headerGap: '22px', logoHeight: '52px', bandHeight: '1px'
  },

  banner: {
    id: 'banner',
    label: 'Blue Header',
    blurb: 'Full-width colour banner across the top, title reversed out in white.',
    bestFor: 'Receipts and statements that should look unmistakably official.',
    header: 'band', logo: 'left', title: 'in-header',
    table: 'headband', footer: 'band', border: 'none',
    decoration: 'none', align: 'left', watermark: false,
    bodyFont: SANS, headingFont: SANS,
    bodySize: '13px', lineHeight: 1.55,
    titleTransform: 'uppercase', titleSpacing: '1px',
    pagePadding: '0 0 18mm 0', cellPadding: '9px 12px',
    headerGap: '20px', logoHeight: '56px', bandHeight: '92px'
  },

  corporate: {
    id: 'corporate',
    label: 'Corporate',
    blurb: 'Vertical sidebar carrying the church details, wide clean tables beside it.',
    bestFor: 'Financial statements and multi-column reports.',
    header: 'sidebar', logo: 'sidebar', title: 'below-header',
    table: 'striped', footer: 'rule', border: 'none',
    decoration: 'side-accent', align: 'left', watermark: false,
    bodyFont: SANS, headingFont: SANS,
    bodySize: '12.5px', lineHeight: 1.5,
    titleTransform: 'uppercase', titleSpacing: '1.5px',
    pagePadding: '18mm 16mm 18mm 8mm', cellPadding: '8px 11px',
    headerGap: '18px', logoHeight: '48px', bandHeight: '46mm'
  },

  diagonal: {
    id: 'diagonal',
    label: 'Diagonal',
    blurb: 'Angular two-tone wedge across the header. Modern invoice styling.',
    bestFor: 'Payment receipts and vouchers.',
    header: 'diagonal', logo: 'left', title: 'in-header',
    table: 'lined', footer: 'rule', border: 'none',
    decoration: 'corner-triangle', align: 'left', watermark: false,
    bodyFont: SANS, headingFont: SANS,
    bodySize: '13px', lineHeight: 1.55,
    titleTransform: 'uppercase', titleSpacing: '2px',
    pagePadding: '0 16mm 18mm 16mm', cellPadding: '9px 12px',
    headerGap: '22px', logoHeight: '54px', bandHeight: '108px'
  },

  wave: {
    id: 'wave',
    label: 'Wave Header',
    blurb: 'Curved graphic sweeping across the top edge. Premium feel.',
    bestFor: 'Donation receipts and thank-you documents.',
    header: 'wave', logo: 'left', title: 'in-header',
    table: 'striped', footer: 'band', border: 'none',
    decoration: 'none', align: 'left', watermark: false,
    bodyFont: SANS_ALT, headingFont: SANS_ALT,
    bodySize: '13px', lineHeight: 1.6,
    titleTransform: 'none', titleSpacing: '0',
    pagePadding: '0 16mm 18mm 16mm', cellPadding: '9px 12px',
    headerGap: '24px', logoHeight: '56px', bandHeight: '120px'
  },

  ribbon: {
    id: 'ribbon',
    label: 'Ribbon',
    blurb: 'Title carried on a colour ribbon, elegant ruled tables beneath.',
    bestFor: 'Certificates and formal registers.',
    header: 'stacked', logo: 'center', title: 'ribbon',
    table: 'lined', footer: 'double-rule', border: 'thin',
    decoration: 'none', align: 'center', watermark: false,
    bodyFont: SERIF, headingFont: SERIF,
    bodySize: '14px', lineHeight: 1.8,
    titleTransform: 'uppercase', titleSpacing: '2px',
    pagePadding: '22mm 18mm', cellPadding: '10px 12px',
    headerGap: '20px', logoHeight: '64px', bandHeight: '40px'
  },

  frame: {
    id: 'frame',
    label: 'Frame',
    blurb: 'Full page border enclosing the whole document. Certificate styling.',
    bestFor: 'Baptism, Confirmation and Marriage certificates.',
    header: 'outline', logo: 'center', title: 'centered-rule',
    table: 'boxed', footer: 'centered-note', border: 'ornamental',
    decoration: 'none', align: 'center', watermark: false,
    bodyFont: SERIF_ALT, headingFont: SERIF_ALT,
    bodySize: '15px', lineHeight: 1.9,
    titleTransform: 'uppercase', titleSpacing: '3px',
    pagePadding: '24mm 20mm', cellPadding: '11px 13px',
    headerGap: '24px', logoHeight: '72px', bandHeight: '3px'
  },

  luxury: {
    id: 'luxury',
    label: 'Luxury',
    blurb: 'Double rules, wide letterspacing and a formal crest. Gold-leaf feel.',
    bestFor: 'Presentation certificates and dedications.',
    header: 'stacked', logo: 'center', title: 'centered-rule',
    table: 'lined', footer: 'double-rule', border: 'double',
    decoration: 'corner-arcs', align: 'center', watermark: true,
    bodyFont: SERIF_ALT, headingFont: SERIF_ALT,
    bodySize: '15px', lineHeight: 2,
    titleTransform: 'uppercase', titleSpacing: '5px',
    pagePadding: '26mm 22mm', cellPadding: '12px 14px',
    headerGap: '28px', logoHeight: '78px', bandHeight: '4px'
  },

  creative: {
    id: 'creative',
    label: 'Creative',
    blurb: 'Gradient header with corner graphics. Contemporary branding.',
    bestFor: 'Youth, choir and outreach documents.',
    header: 'split', logo: 'left', title: 'in-header',
    table: 'striped', footer: 'band', border: 'none',
    decoration: 'corner-arcs', align: 'left', watermark: false,
    bodyFont: SANS, headingFont: SANS,
    bodySize: '13px', lineHeight: 1.55,
    titleTransform: 'uppercase', titleSpacing: '1px',
    pagePadding: '0 16mm 18mm 16mm', cellPadding: '9px 12px',
    headerGap: '22px', logoHeight: '54px', bandHeight: '104px'
  },

  premium: {
    id: 'premium',
    label: 'Premium Professional',
    blurb: 'Header band, footer band, watermark and shaded sections together.',
    bestFor: 'Salary slips, handover documents and anything presented formally.',
    header: 'band', logo: 'left', title: 'boxed',
    table: 'headband', footer: 'band', border: 'thin',
    decoration: 'gradient-bar', align: 'left', watermark: true,
    bodyFont: SANS, headingFont: SERIF,
    bodySize: '13px', lineHeight: 1.6,
    titleTransform: 'uppercase', titleSpacing: '2px',
    pagePadding: '0 0 20mm 0', cellPadding: '10px 13px',
    headerGap: '22px', logoHeight: '58px', bandHeight: '96px'
  }
};

export const DEFAULT_PRINT_TEMPLATE: PrintTemplateId = 'minimal';

/** Ordered plain → most decorated, which is how the picker lists them. */
export const PRINT_TEMPLATE_LIST: PrintTemplate[] = [
  'minimal', 'banner', 'corporate', 'diagonal', 'wave',
  'ribbon', 'frame', 'luxury', 'creative', 'premium'
].map((id) => PRINT_TEMPLATES[id as PrintTemplateId]);

/** Unknown or missing ids fall back rather than rendering an unstyled page. */
export const resolvePrintTemplate = (id?: string): PrintTemplate =>
  PRINT_TEMPLATES[id as PrintTemplateId] || PRINT_TEMPLATES[DEFAULT_PRINT_TEMPLATE];
